import api from './api';

// Role API
export const roleAPI = {
  // Get all roles with filtering and pagination
  getRoles: (params = {}, config = {}) => api.get('/roles', { params, ...config }),

  // Get minimal active roles for selection dropdowns
  getAssignableRoles: () => api.get('/roles/assignable'),

  // Get role hierarchy
  getRoleHierarchy: () => api.get('/roles/hierarchy'),

  // Get role by ID or code (pass { view: 'assignment' } for lightweight role-permissions modal)
  getRole: (identifier, params = {}) => api.get(`/roles/${identifier}`, { params }),

  // Deduplicated assignment-view fetch (avoids Strict Mode double calls)
  _roleAssignmentInflight: new Map(),
  getRoleForAssignment(identifier) {
    const key = String(identifier || '').toUpperCase();
    if (this._roleAssignmentInflight.has(key)) {
      return this._roleAssignmentInflight.get(key);
    }
    const request = api.get(`/roles/${identifier}`, { params: { view: 'assignment' } })
      .finally(() => {
        this._roleAssignmentInflight.delete(key);
      });
    this._roleAssignmentInflight.set(key, request);
    return request;
  },

  // Create new role
  createRole: (roleData) => api.post('/roles', roleData),

  // Update role
  updateRole: (identifier, roleData) => api.put(`/roles/${identifier}`, roleData),

  // Delete role
  deleteRole: (identifier, force = false) => api.delete(`/roles/${identifier}`, {
    params: { force }
  }),

  // Bulk assign permissions to role
  bulkAssignPermissions: (roleCode, permissions, metadata = {}) => 
    api.post(`/roles/${roleCode}/permissions`, {
      permissions,
      metadata
    }),

  // Bulk revoke permissions from role
  bulkRevokePermissions: (roleCode, permissions, reason = '') =>
    api.delete(`/roles/${roleCode}/permissions`, {
      data: { permissions, reason }
    })
};

// Permission API
let permissionCatalogCache = {
  key: '',
  data: null,
  fetchedAt: 0,
  inflight: null,
};
const PERMISSION_CATALOG_TTL_MS = 5 * 60 * 1000;

export const permissionAPI = {
  // Get all permissions with filtering and pagination
  getPermissions: (params = {}, config = {}) => api.get('/permissions', { params, ...config }),

  // Fetch all pages of permissions (for role assignment UI) — uses lightweight catalog endpoint
  fetchAllPermissions: async (params = {}, { force = false } = {}) => {
    const cacheKey = JSON.stringify({
      isActive: params.isActive !== undefined ? params.isActive : true,
      module: params.module || '',
      search: params.search || '',
    });

    if (
      !force
      && permissionCatalogCache.data
      && permissionCatalogCache.key === cacheKey
      && (Date.now() - permissionCatalogCache.fetchedAt) < PERMISSION_CATALOG_TTL_MS
    ) {
      return permissionCatalogCache.data;
    }

    // Deduplicate concurrent catalog requests (React Strict Mode double-mount, etc.)
    if (
      !force
      && permissionCatalogCache.inflight
      && permissionCatalogCache.key === cacheKey
    ) {
      return permissionCatalogCache.inflight;
    }

    permissionCatalogCache.key = cacheKey;
    permissionCatalogCache.inflight = (async () => {
      const response = await api.get('/permissions/catalog', {
        params: {
          isActive: params.isActive !== undefined ? params.isActive : true,
          ...(params.module ? { module: params.module } : {}),
          ...(params.search ? { search: params.search } : {}),
        },
      });
      const permissions = response.data?.data?.permissions || [];
      permissionCatalogCache = {
        key: cacheKey,
        data: permissions,
        fetchedAt: Date.now(),
        inflight: null,
      };
      return permissions;
    })();

    try {
      return await permissionCatalogCache.inflight;
    } catch (error) {
      permissionCatalogCache.inflight = null;
      throw error;
    }
  },

  clearPermissionCatalogCache: () => {
    permissionCatalogCache = { key: '', data: null, fetchedAt: 0, inflight: null };
  },

  // Distinct modules/actions for filters
  getPermissionFilterOptions: (params = {}) => api.get('/permissions/filter-options', { params }),

  // Export permissions (CSV blob or JSON for client PDF)
  exportPermissions: (params = {}) => {
    const format = params.format || 'csv';
    if (format === 'csv') {
      return api.get('/permissions/export', { params, responseType: 'blob' });
    }
    return api.get('/permissions/export', { params: { ...params, format: 'json' } });
  },

  // Get permission validation rules
  getPermissionValidation: () => api.get('/permissions/validation'),

  // Get permissions by module
  getPermissionsByModule: (module, params = {}) => 
    api.get(`/permissions/modules/${module}`, { params }),

  // Get permission by ID or code
  getPermission: (identifier) => api.get(`/permissions/${identifier}`),

  // Check permission availability
  checkPermissionAvailability: (permissionCode, params = {}) =>
    api.get(`/permissions/${permissionCode}/check`, { params }),

  // Create new permission
  createPermission: (permissionData) => api.post('/permissions', permissionData),

  // Bulk create permissions
  bulkCreatePermissions: (permissions) => 
    api.post('/permissions/bulk', { permissions }),

  // Sync permissions from the backend registry.
  syncPermissions: () => api.post('/permissions/sync', {}, { timeout: 15000 }),
  getPermissionSyncStatus: (jobId) => api.get(`/permissions/sync/${jobId}`, { timeout: 15000 }),

  // Update permission
  updatePermission: (identifier, permissionData) => 
    api.put(`/permissions/${identifier}`, permissionData),

  // Delete permission
  deletePermission: (identifier, force = false) => 
    api.delete(`/permissions/${identifier}`, { params: { force } })
};

// User Role Management API
export const userRoleAPI = {
  // Get user roles
  getUserRoles: (userId) => api.get(`/users/${userId}/roles`),

  // Get user permissions
  getUserPermissions: (userId) => api.get(`/users/${userId}/permissions`),

  // Assign role to user
  assignRole: (userId, roleCode, data = {}) => 
    api.post(`/users/${userId}/roles`, { roleCode, ...data }),

  // Revoke role from user
  revokeRole: (userId, roleCode, reason = '') =>
    api.delete(`/users/${userId}/roles/${roleCode}`, {
      data: { reason }
    }),

  // Update user role conditions
  updateRoleConditions: (userId, roleCode, conditions) =>
    api.put(`/users/${userId}/roles/${roleCode}`, { conditions }),

  // Get users by role
  getUsersByRole: (roleCode, params = {}) =>
    api.get(`/roles/${roleCode}/users`, { params })
};

// RBAC Analytics API
export const rbacAnalyticsAPI = {
  // Get role usage statistics
  getRoleStats: () => api.get('/analytics/roles'),

  // Get permission usage statistics
  getPermissionStats: () => api.get('/analytics/permissions'),

  // Get user role distribution
  getUserRoleDistribution: () => api.get('/analytics/user-roles'),

  // Get access patterns
  getAccessPatterns: (params = {}) => api.get('/analytics/access-patterns', { params }),

  // Get audit trail
  getAuditTrail: (params = {}) => api.get('/analytics/audit-trail', { params }),

  // Get access and RBAC history logs
  getAccessLogs: (params = {}) => api.get('/users/access-logs', { params })
};

// RBAC Utilities
export const rbacUtils = {
  /**
   * Check if permission code is valid
   */
  isValidPermissionCode: (code) => {
    const pattern = /^[A-Za-z0-9_.:-]+$/;
    return pattern.test(code);
  },

  /**
   * Parse permission code into components
   */
  parsePermissionCode: (code) => {
    if (!rbacUtils.isValidPermissionCode(code)) {
      return null;
    }

    const separator = code.includes('.') ? '.' : ':';
    const parts = code.split(separator).filter(Boolean);

    if (parts.length < 3) {
      return null;
    }

    return {
      module: parts[0].toUpperCase(),
      resource: parts.slice(1, -1).join('_').toUpperCase(),
      action: parts[parts.length - 1].toUpperCase()
    };
  },

  /**
   * Build permission code from components
   */
  buildPermissionCode: (module, resource, action) => {
    return `${module.toUpperCase()}:${resource.toUpperCase()}:${action.toUpperCase()}`;
  },

  /**
   * Get role level name
   */
  getRoleLevelName: (level) => {
    const levels = {
      1: 'Critical',
      2: 'High',
      3: 'Management',
      4: 'Supervision',
      5: 'Standard',
      6: 'Basic'
    };
    return levels[level] || 'Unknown';
  },

  /**
   * Get permission category display name
   */
  getPermissionCategoryName: (category) => {
    const categories = {
      'system-admin': 'System Administration',
      'data-access': 'Data Access',
      'business-logic': 'Business Operations',
      'reporting': 'Reports & Analytics',
      'configuration': 'Configuration'
    };
    return categories[category] || category;
  },

  /**
   * Sort roles by hierarchy level
   */
  sortRolesByHierarchy: (roles) => {
    return [...roles].sort((a, b) => (a?.level ?? Infinity) - (b?.level ?? Infinity));
  },

  /**
   * Group permissions by module
   */
  groupPermissionsByModule: (permissions) => {
    return permissions.reduce((groups, permission) => {
      const module = permission.module;
      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(permission);
      return groups;
    }, {});
  },

  /**
   * Filter permissions by category
   */
  filterPermissionsByCategory: (permissions, category) => {
    return permissions.filter(p => p.category === category);
  },

  /**
   * Check if role can inherit from another role
   */
  canRoleInheritFrom: (childRole, parentRole) => {
    return childRole.level > parentRole.level;
  },

  /**
   * Format role assignment conditions
   */
  formatRoleConditions: (conditions) => {
    if (!conditions || Object.keys(conditions).length === 0) {
      return 'No conditions';
    }

    const formatted = [];
    Object.entries(conditions).forEach(([key, value]) => {
      formatted.push(`${key}: ${JSON.stringify(value)}`);
    });

    return formatted.join(', ');
  },

  /**
   * Validate role assignment data
   */
  validateRoleAssignment: (data) => {
    const errors = {};

    if (!data.roleCode) {
      errors.roleCode = 'Role code is required';
    }

    if (data.expiresAt && new Date(data.expiresAt) <= new Date()) {
      errors.expiresAt = 'Expiration date must be in the future';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  /**
   * Calculate effective permissions for roles
   */
  calculateEffectivePermissions: (userRoles, rolePermissions) => {
    const allPermissions = new Set();

    userRoles.forEach(userRole => {
      const rolePerms = rolePermissions[userRole.code] || [];
      rolePerms.forEach(perm => {
        allPermissions.add(perm.code);
      });
    });

    return Array.from(allPermissions);
  },

  /**
   * Check permission conflicts
   */
  checkPermissionConflicts: (permissions) => {
    const conflicts = [];
    const permissionMap = new Map();

    permissions.forEach(permission => {
      const key = `${permission.module}:${permission.resource}`;
      
      if (!permissionMap.has(key)) {
        permissionMap.set(key, []);
      }
      
      permissionMap.get(key).push(permission);
    });

    permissionMap.forEach((perms, key) => {
      if (perms.length > 1) {
        // Check for conflicting actions
        const actions = perms.map(p => p.action);
        if (actions.includes('MANAGE') && actions.length > 1) {
          conflicts.push({
            resource: key,
            conflict: 'MANAGE permission conflicts with specific actions',
            permissions: perms
          });
        }
      }
    });

    return conflicts;
  }
};

export default {
  roleAPI,
  permissionAPI,
  userRoleAPI,
  rbacAnalyticsAPI,
  rbacUtils
};